import { OrderDishReviews } from './DishReviews'
import { PaystackModal } from './PaystackModal'
import { payForOrder } from '../../lib/paystack'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalBadge, MinimalButton, MinimalCard, MinimalEmptyState, type MinimalTone } from '@ovasabi/ui-minimal'
import { from, minimalVars } from '@ovasabi/ui-minimal/tokens'
import { ListSkeleton, Money, PageHeader, Screen, FilterPills } from '../../components/chow'
import { BasketIcon } from '../../components/icons'
import { useMarketplaceOrderSnapshot } from '../../generated/prototypeRuntime'
import { isLiveRuntime } from '../../lib/runtime'
import { useProjectionReady } from '../../lib/projectionHealth'
import { enumShort } from './chefShared'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { formatOrderWhen, isUnpaidMealPlanHold, orderReference, useOrderItemNames } from './orderDisplay'
export { isUnpaidMealPlanHold }
import { useKitchenNames } from './projections'
import { HygieneReport } from './HygieneReport'
import { useUnreadOrdersMap } from './unreadMessages'
import { holdCutoffMs } from './mealPlanning'
import { compareTimestamps } from '@ovasabi/frontend-kit'

/**
 * Customer order tracking. Lists the signed-in customer's orders from the
 * `marketplace.marketplace_orders` projection (backed by `choosechow_orders`).
 *
 * Checkout is per kitchen, so one basket becomes one order **per chef**. The
 * card used to show a reference, a status and a total and nothing else — so a
 * ₦87,000 basket from three kitchens looked like it had shrunk to a single
 * ₦13,000 order, with no way to see what was in it or where the rest went.
 * Each card now names its kitchen and its dishes, breaks the money down, and
 * orders placed in one checkout are grouped under a header carrying the total
 * that was actually charged.
 */

/** choosechow_orders.status → customer-facing label + badge tone. */
const ORDER_STATUS: Record<string, { label: string; tone: MinimalTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  pending_payment: { label: 'Awaiting payment', tone: 'warning' },
  paid: { label: 'Paid', tone: 'info' },
  accepted: { label: 'Confirmed', tone: 'success' },
  preparing: { label: 'Preparing', tone: 'warning' },
  ready: { label: 'Ready', tone: 'success' },
  delivery_requested: { label: 'Kitchen arranging delivery', tone: 'warning' },
  in_delivery: { label: 'With delivery person · kitchen update', tone: 'info' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
  refunded: { label: 'Refunded', tone: 'neutral' },
}

const orderStatus = (raw: unknown): { label: string; tone: MinimalTone } => {
  const key = enumShort(raw, 'pending_payment')
  return ORDER_STATUS[key] ?? { label: key.replace(/_/g, ' '), tone: 'neutral' }
}

const ORDER_PAYMENT_TTL_MS = 45 * 60 * 1000

const isExpiredUnpaidOrder = (order: OrderRow): boolean => {
  const status = enumShort(order.status)
  const reason = String(order.status_reason ?? '').toLowerCase()
  if (status === 'cancelled' && reason === 'expired') return true
  if (status !== 'pending_payment') return false
  const raw = String(order.scheduled_at ?? '')
  if (!raw) {
    const openedAt = new Date(String(order.created_at ?? order.updated_at ?? '')).getTime()
    if (!Number.isFinite(openedAt)) return false
    return Date.now() - openedAt > ORDER_PAYMENT_TTL_MS
  }
  const when = new Date(raw)
  if (Number.isNaN(when.getTime())) return false
  return Date.now() > holdCutoffMs(when, false)
}

const expiredOrderLabel = (order: OrderRow): string =>
  String(order.scheduled_at ?? '').trim() ? 'Expired hold' : 'Payment expired'

const deliveryLabel = (raw: unknown): string => {
  const key = String(raw ?? '').toLowerCase().replace(/^delivery_choice_/, '')
  const labels: Record<string, string> = {
    standard: 'Standard delivery', express: 'Express delivery', chowdash: 'Delivery',
    self_pickup: 'Collect from kitchen', recurring: 'Scheduled delivery',
  }
  return labels[key] ?? 'Delivery'
}

/** Orders created within this window of each other came from one checkout. */
const SAME_CHECKOUT_MS = 2 * 60 * 1000

type OrderRow = Record<string, unknown>

interface CheckoutGroup {
  key: string
  placedAt: string
  orders: readonly OrderRow[]
  totalMinor: number
}

/**
 * Group orders by the checkout that produced them. Nothing on the row ties the
 * per-kitchen orders of one basket together — no batch id exists — so they are
 * grouped by placement time, which is what the customer remembers anyway ("the
 * order I placed at 7pm"). A wrong guess costs a heading, never a number: each
 * order still shows its own kitchen, items and total.
 */
const groupByCheckout = (orders: readonly OrderRow[]): readonly CheckoutGroup[] => {
  const groups: CheckoutGroup[] = []
  for (const order of orders) {
    const placedAt = String(order.created_at ?? '')
    const at = new Date(placedAt).getTime()
    const last = groups[groups.length - 1]
    const lastAt = last ? new Date(last.placedAt).getTime() : NaN
    const sameCheckout =
      last &&
      Number.isFinite(at) &&
      Number.isFinite(lastAt) &&
      Math.abs(lastAt - at) <= SAME_CHECKOUT_MS
    if (sameCheckout) {
      groups[groups.length - 1] = {
        ...last,
        orders: [...last.orders, order],
        totalMinor: last.totalMinor + Number(order.total_minor ?? 0),
      }
      continue
    }
    groups.push({
      key: String(order.id ?? groups.length),
      placedAt,
      orders: [order],
      totalMinor: Number(order.total_minor ?? 0),
    })
  }
  return groups
}

interface HorizonBucket {
  title: string
  subtitle: string
  groups: readonly CheckoutGroup[]
}

const groupIntoHorizons = (groups: readonly CheckoutGroup[]): readonly HorizonBucket[] => {
  if (groups.length === 0) return []
  const now = Date.now()
  const oneWeek = 7 * 24 * 60 * 60 * 1000

  const thisWeek: CheckoutGroup[] = []
  const lastWeek: CheckoutGroup[] = []
  const earlier: CheckoutGroup[] = []

  for (const group of groups) {
    const at = new Date(group.placedAt).getTime()
    if (!Number.isFinite(at)) {
      earlier.push(group)
      continue
    }
    const diff = now - at
    if (diff <= oneWeek) {
      thisWeek.push(group)
    } else if (diff <= 2 * oneWeek) {
      lastWeek.push(group)
    } else {
      earlier.push(group)
    }
  }

  const buckets: HorizonBucket[] = []
  if (thisWeek.length > 0) {
    const total = thisWeek.reduce((s, g) => s + g.totalMinor, 0)
    const count = thisWeek.reduce((s, g) => s + g.orders.length, 0)
    buckets.push({
      title: 'This Week',
      subtitle: `${count} ${count === 1 ? 'order' : 'orders'} · Total ₦${(total / 100).toLocaleString()}`,
      groups: thisWeek,
    })
  }
  if (lastWeek.length > 0) {
    const total = lastWeek.reduce((s, g) => s + g.totalMinor, 0)
    const count = lastWeek.reduce((s, g) => s + g.orders.length, 0)
    buckets.push({
      title: 'Last Week',
      subtitle: `${count} ${count === 1 ? 'order' : 'orders'} · Total ₦${(total / 100).toLocaleString()}`,
      groups: lastWeek,
    })
  }
  if (earlier.length > 0) {
    const total = earlier.reduce((s, g) => s + g.totalMinor, 0)
    const count = earlier.reduce((s, g) => s + g.orders.length, 0)
    buckets.push({
      title: 'Earlier Orders',
      subtitle: `${count} ${count === 1 ? 'order' : 'orders'} · Total ₦${(total / 100).toLocaleString()}`,
      groups: earlier,
    })
  }
  return buckets
}

/**
 * Warning tones, mixed off the `warning` token. Hoisted to consts because
 * linaria drops a `color-mix` written inside a props-dependent interpolation.
 */
const WARNING_TINT = `color-mix(in srgb, ${minimalVars.color.warning} 30%, transparent)`
/* The raw #b45309 this replaces reached only 5.0:1. This mix reaches 5.7:1. */
const WARNING_INK = `color-mix(in srgb, ${minimalVars.color.warning} 50%, ${minimalVars.color.textPrimary})`

const Style = {
  SummaryBar: styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${minimalVars.space.xs};
    margin: 0;

    /* 640px matches no named stop. The tiles go to four-up before the page stop. */
    @media (min-width: 640px) {
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: ${minimalVars.space.sm};
    }
  `,
  SummaryCard: styled.div<{ $tone?: 'warning' | 'default' }>`
    min-width: 0;
    overflow-wrap: anywhere;
    background: ${({ $tone }) => ($tone === 'warning' ? minimalVars.color.warningSoft : minimalVars.color.bgSurface)};
    border: 1px solid ${({ $tone }) => ($tone === 'warning' ? WARNING_TINT : minimalVars.color.borderSubtle)};
    border-radius: ${minimalVars.radius.md};
    padding: 12px ${minimalVars.space.sm};
    display: flex;
    flex-direction: column;
    gap: ${minimalVars.space["2xs"]};
    box-shadow: ${minimalVars.shadow.subtle};
  `,
  SummaryLabel: styled.span`
    font-size: ${minimalVars.typography.metaSize};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${minimalVars.color.textTertiary};
  `,
  SummaryValue: styled.span<{ $tone?: 'warning' | 'default' }>`
    /* Summary figures stay at 1.15rem: h2Size is 22px and overflows the 180px summary tile. */
    font-size: 1.15rem;
    font-weight: 800;
    font-family: ${minimalVars.typography.displayFamily};
    color: ${({ $tone }) => ($tone === 'warning' ? WARNING_INK : minimalVars.color.textPrimary)};
  `,
  HorizonSection: styled.div`
    margin-bottom: ${minimalVars.space.lg};
  `,
  HorizonHeader: styled.div`
    margin-bottom: ${minimalVars.space.sm};
    padding-bottom: ${minimalVars.space["2xs"]};
    border-bottom: 2px solid ${minimalVars.color.borderSubtle};
  `,
  HorizonTitle: styled.h2`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    /* Horizon titles are section heads, so they share the app-wide 22px from h2Size. */
    font-size: ${minimalVars.typography.h2Size};
    font-weight: 800;
    color: ${minimalVars.color.textPrimary};
  `,
  HorizonSubtitle: styled.p`
    margin: 2px 0 0 0;
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textSecondary};
  `,
  Group: styled.section`
    display: grid;
    gap: ${minimalVars.space.xs};
    margin-bottom: ${minimalVars.space.sm};
  `,
  GroupHead: styled.div<{ $single?: boolean }>`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
    max-width: ${({ $single }) => ($single ? '460px' : '100%')};
    padding-bottom: 2px;
  `,
  GroupTitle: styled.h3`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    /* bodySize matches 0.95rem; captionSize is 13px and sits under the 22px horizon title. */
    font-size: ${minimalVars.typography.bodySize};
    font-weight: 700;
    color: ${minimalVars.color.textPrimary};
  `,
  List: styled.div<{ $single?: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${minimalVars.space.sm};
    align-items: start;
    max-width: ${({ $single }) => ($single ? '460px' : '100%')};

    ${from('page')} {
      grid-template-columns: ${({ $single }) => ($single ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))')};
    }
  `,
  Card: styled(MinimalCard)`
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: ${minimalVars.color.bgSurface};
    border: 1px solid ${minimalVars.color.borderSubtle};
    padding: ${minimalVars.space.sm};
    > div { display: flex; flex-direction: column; gap: ${minimalVars.space.sm}; }
    border-radius: 16px;
    box-shadow: ${minimalVars.shadow.subtle};
    transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease;
    &:hover {
      border-color: ${minimalVars.color.borderStrong};
      box-shadow: ${minimalVars.shadow.medium};
    }
  `,
  OlderToggleWrap: styled.div`
    display: flex;
    justify-content: center;
    margin: ${minimalVars.space.sm} 0 ${minimalVars.space.md};
  `,
  OlderToggleBtn: styled(MinimalButton)`
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    padding: 10px 22px;
    border-radius: ${minimalVars.radius.pill};
    border: 1px solid ${minimalVars.color.borderStrong};
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textPrimary};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: 700;
    cursor: pointer;
    box-shadow: ${minimalVars.shadow.subtle};
    transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease, transform 160ms ease;

    &:hover {
      background: ${minimalVars.color.bgSurfaceHover};
      border-color: ${minimalVars.color.brand};
      color: ${minimalVars.color.brand};
      transform: translateY(-1px);
    }
  `,
  CardBody: styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
  `,
  UnreadAlert: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: ${minimalVars.space["2xs"]} 10px;
    border-radius: ${minimalVars.radius.sm};
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightBold};
    margin-bottom: 6px;
  `,
  Top: styled.div`
    display: flex;
    flex-wrap: wrap;
    > * { min-width: 0; }
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  Kitchen: styled.h4`
    margin: 0;
    /* Kitchen names stay at 1.1rem: h2Size is 22px and wraps names in a 320px card column. */
    font-size: 1.1rem;
    font-weight: 700;
    color: ${minimalVars.color.textPrimary};
    line-height: 1.3;
    word-break: break-word;
  `,
  Meta: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Items: styled.ul`
    margin: ${minimalVars.space.sm} 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: ${minimalVars.space.xs};
    color: ${minimalVars.color.textSecondary};
    /* Dish lines stay at 0.9rem: captionSize is 13px, so the list drops under its own price rows. */
    font-size: 0.9rem;
    line-height: 1.4;
  `,
  CardFooter: styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  Money: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    padding-top: ${minimalVars.space.sm};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
  `,
  MoneyRow: styled.div`
    display: flex;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  TotalRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    margin-top: ${minimalVars.space["2xs"]};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  ActionRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    flex-wrap: wrap;
    padding-top: ${minimalVars.space["2xs"]};
    > button {
      min-height: 38px;
    }
  `,
  Foot: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    margin-top: 10px;
    flex-wrap: wrap;
  `,
}

export const OrdersPage = () => {
  const [filter, setFilter] = useState<'all' | 'active' | 'past' | 'archived'>('all')
  const [showOlder, setShowOlder] = useState(false)
  const [paymentOrder, setPaymentOrder] = useState<OrderRow | null>(null)
  const navigate = useNavigate()
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const orders = useMarketplaceOrderSnapshot() as { records: readonly OrderRow[] }
  const kitchenName = useKitchenNames()
  const unreadMap = useUnreadOrdersMap(profileId, 'customer')

  const ordersReady = useProjectionReady('marketplace', 'marketplace_orders')
  const myOrders = useMemo(() => {
    let list = orders.records.filter((order) => !isUnpaidMealPlanHold(order))
    if (isLiveRuntime()) {
      // Live: only this customer's real orders (drop optimistic/prototype rows).
      list = list.filter((order) => {
        const id = String(order.id ?? '')
        if (id.startsWith('order-') || id.includes('-prototype')) return false
        return String(order.customer_profile_id ?? '') === profileId
      })
    }
    return list
      .slice()
      .sort((a, b) => compareTimestamps(a.created_at, b.created_at, String(a.id ?? ''), String(b.id ?? ''), { direction: 'desc' }))
  }, [orders.records, profileId])

  const summaryMetrics = useMemo(() => {
    let total = 0
    let awaitingPayment = 0
    let awaitingPaymentMinor = 0
    let inProgress = 0
    let delivered = 0
    let archived = 0
    for (const o of myOrders) {
      const st = enumShort(o.status)
      if (isExpiredUnpaidOrder(o)) {
        archived += 1
        continue
      }
      // `total` counts what the customer can still act on. An expired hold is a
      // real row and is reported on its own, but it is not a live order.
      total += 1
      if (st === 'pending_payment') {
        awaitingPayment += 1
        awaitingPaymentMinor += Number(o.total_minor ?? 0)
      } else if (['completed', 'refunded'].includes(st)) {
        delivered += 1
      } else {
        inProgress += 1
      }
    }
    return { total, awaitingPayment, awaitingPaymentMinor, inProgress, delivered, archived }
  }, [myOrders])

  // Dish names live on the order detail read, not the lean projection.
  const itemNames = useOrderItemNames(myOrders.map((order) => String(order.id ?? '')))
  const groups = useMemo(() => groupByCheckout(myOrders.filter((order) => {
    const archived = isExpiredUnpaidOrder(order)
    const past = ['completed', 'cancelled', 'refunded'].includes(enumShort(order.status))
    if (filter === 'archived') return archived
    if (filter === 'past') return past && !archived
    if (filter === 'active') return !past && !archived
    return !archived
  })), [myOrders, filter])

  const horizons = useMemo(() => groupIntoHorizons(groups), [groups])
  const thisWeekHorizon = useMemo(() => horizons.find((h) => h.title === 'This Week'), [horizons])
  const olderHorizons = useMemo(() => horizons.filter((h) => h.title !== 'This Week'), [horizons])
  const olderOrdersCount = useMemo(
    () => olderHorizons.reduce((sum, h) => sum + h.groups.reduce((gSum, g) => gSum + g.orders.length, 0), 0),
    [olderHorizons],
  )
  const isOlderVisible = showOlder || filter === 'past' || !thisWeekHorizon

  const renderHorizon = (horizon: HorizonBucket) => (
    <Style.HorizonSection key={horizon.title}>
      <Style.HorizonHeader>
        <Style.HorizonTitle>{horizon.title}</Style.HorizonTitle>
        <Style.HorizonSubtitle>{horizon.subtitle}</Style.HorizonSubtitle>
      </Style.HorizonHeader>

      {horizon.groups.map((group) => {
        const isSingle = group.orders.length === 1
        return (
          <Style.Group key={group.key}>
            <Style.GroupHead $single={isSingle}>
              <Style.GroupTitle>
                {formatOrderWhen(group.placedAt) || 'Recent order'}
              </Style.GroupTitle>
              <Style.Meta>
                {group.orders.length > 1
                  ? `${group.orders.length} kitchens · total ₦${(group.totalMinor / 100).toLocaleString()}`
                  : `Total ₦${(group.totalMinor / 100).toLocaleString()}`}
              </Style.Meta>
            </Style.GroupHead>

            <Style.List $single={isSingle}>
              {group.orders.map((order) => {
                const id = String(order.id ?? '')
                const status = enumShort(order.delivery_choice) === 'self_pickup' && enumShort(order.status) === 'completed'
                  ? { label: 'Collected', tone: 'success' as const }
                  : isExpiredUnpaidOrder(order)
                    ? { label: expiredOrderLabel(order), tone: 'neutral' as const }
                    : orderStatus(order.status)
                const items = itemNames[id] ?? []
                const itemsResolved = itemNames[id] !== undefined
                const summary = String(order.summary ?? '').trim()
                const subtotal = Number(order.subtotal_minor ?? 0)
                const total = Number(order.total_minor ?? 0)
                // ChooseChow charges no delivery until ChowDash dispatch is live. A
                // few historical orders still carry one inside their total; it
                // stays in the arithmetic (so savings are right) but is never
                // shown as a line, and those unpaid orders go to support.
                const legacyCharge = Number(order.delivery_fee_minor ?? 0)
                const serviceFee = Number(order.service_fee_minor ?? 0)
                const discount = Math.max(0, subtotal + legacyCharge + serviceFee - total)
                const kitchen = kitchenName(String(order.merchant_profile_id ?? ''))

                return (
                  <Style.Card key={id} role="article" aria-label={orderReference(order)}>
                    <Style.CardBody>
                      <Style.Top>
                        <Style.Kitchen>{kitchen}</Style.Kitchen>
                        <MinimalBadge tone={status.tone} emphasis="soft" size="sm">
                          {status.label}
                        </MinimalBadge>
                      </Style.Top>
                      <Style.Meta>
                        {orderReference(order)}
                        {order.delivery_choice ? ` · ${deliveryLabel(order.delivery_choice)}` : ''}
                      </Style.Meta>

                      <Style.Items>
                        {items.length > 0 ? (
                          items.map((line) => <li key={line}>{line}</li>)
                        ) : summary ? (
                          <li>{summary}</li>
                        ) : (
                          <li>
                            <Style.Meta>
                              {isLiveRuntime() && !itemsResolved
                                ? 'Loading what you ordered…'
                                : `${Number(order.item_count ?? 0) || 'Some'} item(s)`}
                            </Style.Meta>
                          </li>
                        )}
                      </Style.Items>
                    </Style.CardBody>

                    {enumShort(order.delivery_choice) === 'self_pickup' && <MinimalAlert
                      tone={['ready', 'completed'].includes(enumShort(order.status)) ? 'success' : 'info'}
                      title={enumShort(order.status) === 'completed' ? 'Meal collected' : enumShort(order.status) === 'ready' ? 'Ready for collection' : 'Kitchen pickup'}>
                      {enumShort(order.status) === 'completed'
                        ? 'The kitchen confirmed you received your meal. If this is incorrect, message the kitchen.'
                        : <>
                          {order.pickup_address ? `${String(order.pickup_address)}. ` : ''}
                          Message the kitchen to agree a collection time, or to share your own rider’s details.
                        </>}
                    </MinimalAlert>}
                    <Style.CardFooter>
                      <Style.Money>
                        <Style.MoneyRow>
                          <span>Food</span>
                          <span>₦{(subtotal / 100).toLocaleString()}</span>
                        </Style.MoneyRow>
                        <Style.MoneyRow>
                          <span>Service fee</span>
                          <Money amount={serviceFee / 100} />
                        </Style.MoneyRow>
                        {discount > 0 && <Style.MoneyRow><span>Savings</span><span>−<Money amount={discount / 100} /></span></Style.MoneyRow>}
                        <Style.TotalRow>
                          <span>This kitchen</span>
                          <Money amount={total / 100} tone="brand" />
                        </Style.TotalRow>
                      </Style.Money>

                      {enumShort(order.status, '') === 'pending_payment' && legacyCharge === 0 && !isExpiredUnpaidOrder(order) && (
                        <MinimalButton
                          style={{ width: '100%', minHeight: 44 }}
                          onClick={() => setPaymentOrder(order)}
                        >
                          Complete payment · ₦{(total / 100).toLocaleString()}
                        </MinimalButton>
                      )}
                      {enumShort(order.status, '') === 'pending_payment' && legacyCharge > 0 && <MinimalAlert tone="warning" title="This order needs a quick review">It was priced under an older plan. Contact support before you pay and we’ll sort it out.</MinimalAlert>}
                      {unreadMap[id]?.unreadCount ? (
                        <Style.UnreadAlert>
                          💬 {unreadMap[id].unreadCount} new message{unreadMap[id].unreadCount === 1 ? '' : 's'}
                        </Style.UnreadAlert>
                      ) : null}
                      <Style.ActionRow>
                        <Style.Meta>
                          {order.scheduled_at
                            ? `Scheduled ${formatOrderWhen(order.scheduled_at)}`
                            : formatOrderWhen(order.created_at)}
                        </Style.Meta>
                        <MinimalButton
                          size="sm"
                          variant={unreadMap[id]?.unreadCount ? 'primary' : 'secondary'}
                          onClick={() => navigate(`/inbox?order=${encodeURIComponent(id)}`)}
                        >
                          {unreadMap[id]?.unreadCount
                            ? `Reply (${unreadMap[id].unreadCount})`
                            : 'Message this kitchen'}
                        </MinimalButton>
                      </Style.ActionRow>
                      {enumShort(order.status) === 'completed' && <OrderDishReviews orderId={id} profileId={profileId} />}
                      {/* The server accepts reports only on the customer's own finished orders. */}
                      {['completed', 'refunded'].includes(enumShort(order.status, '')) ? (
                        <HygieneReport orderId={id} />
                      ) : null}
                    </Style.CardFooter>
                  </Style.Card>
                )
              })}
            </Style.List>
          </Style.Group>
        )
      })}
    </Style.HorizonSection>
  )

  return (
    <Screen header={<PageHeader title="Orders" accentDot onBack={back} />}>
      <FilterPills ariaLabel="Filter orders" value={filter} onChange={setFilter} options={[
        { value: 'all', label: 'All orders' }, { value: 'active', label: 'In progress' }, { value: 'past', label: 'Past orders' }, { value: 'archived', label: 'Expired' },
      ]} />

      {summaryMetrics.total > 0 && (
        <Style.SummaryBar>
          <Style.SummaryCard>
            {/* Live orders only. Expired holds are real rows but they are not
                orders the customer can act on, and counting them here made an
                abandoned checkout look like something still owed. */}
            <Style.SummaryLabel>All Orders</Style.SummaryLabel>
            <Style.SummaryValue>{summaryMetrics.total}</Style.SummaryValue>
          </Style.SummaryCard>
          {summaryMetrics.awaitingPayment > 0 && (
            <Style.SummaryCard $tone="warning">
              <Style.SummaryLabel>Awaiting Payment</Style.SummaryLabel>
              <Style.SummaryValue $tone="warning">
                {summaryMetrics.awaitingPayment} · ₦{(summaryMetrics.awaitingPaymentMinor / 100).toLocaleString()}
              </Style.SummaryValue>
            </Style.SummaryCard>
          )}
          <Style.SummaryCard>
            <Style.SummaryLabel>In Progress</Style.SummaryLabel>
            <Style.SummaryValue>{summaryMetrics.inProgress}</Style.SummaryValue>
          </Style.SummaryCard>
          <Style.SummaryCard>
            <Style.SummaryLabel>Completed</Style.SummaryLabel>
            <Style.SummaryValue>{summaryMetrics.delivered}</Style.SummaryValue>
          </Style.SummaryCard>
          {summaryMetrics.archived > 0 && (
            <Style.SummaryCard>
              <Style.SummaryLabel>Expired</Style.SummaryLabel>
              <Style.SummaryValue>{summaryMetrics.archived}</Style.SummaryValue>
            </Style.SummaryCard>
          )}
        </Style.SummaryBar>
      )}

      {!ordersReady && horizons.length === 0 ? (
        <ListSkeleton rows={3} label="Loading your orders" />
      ) : horizons.length === 0 ? (
        <MinimalEmptyState
          icon={<BasketIcon />}
          title={myOrders.length === 0 ? 'No orders yet' : filter === 'active' ? 'You are all caught up' : filter === 'archived' ? 'Nothing expired' : 'No past orders yet'}
          description={myOrders.length === 0 ? "Your orders show here, with updates as the kitchen prepares them." : "Choose another view to see your other orders."}
          action={<MinimalButton onClick={() => navigate('/dishes')}>Browse dishes</MinimalButton>}
        />
      ) : (
        <>
          {thisWeekHorizon && renderHorizon(thisWeekHorizon)}

          {olderHorizons.length > 0 && thisWeekHorizon && (
            <Style.OlderToggleWrap>
              <Style.OlderToggleBtn variant="secondary"
                type="button"
                onClick={() => setShowOlder((prev) => !prev)}
              >
                {isOlderVisible
                  ? 'Hide older orders'
                  : `View older orders (${olderOrdersCount})`}
              </Style.OlderToggleBtn>
            </Style.OlderToggleWrap>
          )}

          {isOlderVisible && olderHorizons.map((horizon) => renderHorizon(horizon))}
        </>
      )}
      <PaystackModal open={paymentOrder !== null}
        amountMinor={Math.max(0, Number(paymentOrder?.total_minor ?? 0) - Number(paymentOrder?.amount_paid_minor ?? 0))}
        onClose={() => setPaymentOrder(null)}
        onSuccess={async () => { if (paymentOrder) await payForOrder(String(paymentOrder.id), profileId, Math.max(0, Number(paymentOrder.total_minor ?? 0) - Number(paymentOrder.amount_paid_minor ?? 0))) }}
      />
    </Screen>
  )
}
